import {ToggleButtonGroup} from '@wandb/components';
import {useOpVersionOptions} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/CallsPage/callsTableFilter';
import {OpSelector} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/CallsPage/OpSelector';
import React, {useCallback} from 'react';

import {WeaveAlertAnalytics} from '../analytics';
import {WeaveMetricThresholdActions} from '../store';
import {type SourceType, type WeaveMetricThresholdConfig} from '../types';
import {type MonitorSelection, MonitorSelector} from './MonitorSelector';
import {SpanFilterSelector} from './SpanFilterSelector';

type SourceSelectorProps = {
  entityName: string;
  projectName: string;
  metricFilter: WeaveMetricThresholdConfig['metricFilter'];
};

const SOURCE_ITEMS: Array<{id: SourceType; children: string}> = [
  {id: 'OPERATION', children: 'Operation'},
  {id: 'MONITOR', children: 'Monitor'},
  {id: 'SPANS', children: 'Agent spans'},
];

const isSourceType = (value: unknown): value is SourceType =>
  SOURCE_ITEMS.some(item => item.id === value);

export const SourceSelector = ({
  entityName,
  projectName,
  metricFilter,
}: SourceSelectorProps) => {
  const sourceType = metricFilter.sourceType || 'OPERATION';

  return (
    <>
      <div className="mb-12">
        <ToggleButtonGroup
          aria-label="Metric source"
          items={SOURCE_ITEMS}
          selectedKeys={[sourceType]}
          selectionMode="single"
          disallowEmptySelection
          size="md"
          onSelectionChange={keys => {
            const next = Array.from(keys)[0];
            if (isSourceType(next) && next !== sourceType) {
              WeaveAlertAnalytics.sourceSelected({sourceType: next});
              WeaveMetricThresholdActions.setSourceType(next);
            }
          }}
        />
      </div>

      {sourceType === 'MONITOR' && (
        <MonitorSourceSelector
          entityName={entityName}
          projectName={projectName}
          monitorRef={metricFilter.monitorRef || ''}
        />
      )}
      {sourceType === 'SPANS' && (
        <>
          <SpanSourceNote />
          <div className="mt-12">
            <SpanFilterSelector
              projectId={`${entityName}/${projectName}`}
              operationName={metricFilter.spanOperationName}
              spanName={metricFilter.spanName}
              agentName={metricFilter.spanAgentName}
            />
          </div>
        </>
      )}
      {sourceType === 'OPERATION' && (
        <OperationSourceSelector
          entityName={entityName}
          projectName={projectName}
          opStr={metricFilter.opStr || ''}
        />
      )}
    </>
  );
};

const SpanSourceNote = () => (
  <div className="text-sm text-wb-text-tertiary">
    Evaluated across agent spans in this project. Optionally narrow to a span
    type below.
  </div>
);

type OperationSourceSelectorProps = {
  entityName: string;
  projectName: string;
  opStr: string;
};

const OperationSourceSelector = ({
  entityName,
  projectName,
  opStr,
}: OperationSourceSelectorProps) => {
  // OpSelector expects an opVersionRefs array; derive it from the single opStr
  const opVersionRefs = opStr ? [opStr] : [];
  const opFilter = {opVersionRefs};
  const opVersionOptions = useOpVersionOptions(
    entityName,
    projectName,
    opFilter,
    false
  );

  return (
    <>
      <div className="font-semibold">Op name</div>
      <div className="mt-4">
        <OpSelector
          frozenFilter={undefined}
          filter={opFilter}
          setFilter={newFilter => {
            WeaveMetricThresholdActions.setOpStr(
              newFilter.opVersionRefs?.[0] || ''
            );
          }}
          selectedOpVersionOption={opStr}
          opVersionOptions={opVersionOptions}
          useMenuPortalBody={true}
          width="100%"
        />
      </div>
    </>
  );
};

type MonitorSourceSelectorProps = {
  entityName: string;
  projectName: string;
  monitorRef: string;
};

const MonitorSourceSelector = ({
  entityName,
  projectName,
  monitorRef,
}: MonitorSourceSelectorProps) => {
  const handleMonitorChange = useCallback((selection: MonitorSelection) => {
    WeaveMetricThresholdActions.setMonitorRef(
      selection.monitorRef,
      selection.scorerRefs
    );
  }, []);

  return (
    <>
      <div className="font-semibold">Monitor</div>
      <div className="mt-4">
        <MonitorSelector
          entityName={entityName}
          projectName={projectName}
          value={monitorRef}
          onChange={handleMonitorChange}
        />
      </div>
    </>
  );
};
